import { CodeBlock } from '@metorial/code';
import { CodeEditor } from '@metorial/code-editor';
import {
  Button,
  Checkbox,
  DatePicker,
  Input,
  OptionToggle,
  Select,
  Spinner,
  Text,
  theme,
  Title
} from '@metorial/ui';
import qs from 'qs';
import { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { getAllowedApiUrl } from './apiHostAllowlist';

type IntrospectedType = {
  examples: any[];
  items?: IntrospectedType[];
  properties?: Record<string, IntrospectedType>;
  type: string;
  name?: string;
  description?: string;
  optional: boolean;
  nullable: boolean;
};

type ApiVersion = {
  version: string;
  displayVersion: string;
  isCurrent: boolean;
};

type ApiPath = {
  path: string;
  sdkPath: string;
};

type Endpoint = {
  id: string;
  method: string;
  name: string;
  description: string;
  allPaths: ApiPath[];
  queryId?: string | null;
  bodyId?: string | null;
  outputId: string;
  controllerId: string;
  hideInDocs?: boolean;
};

type Controller = {
  id: string;
  name: string;
  description: string;
  deprecated?: boolean;
  hideInDocs?: boolean;
};

type TypeDefinition = {
  id: string;
  name: string;
  type: IntrospectedType;
};

type DocumentationData = {
  versions: ApiVersion[];
  endpoints: Endpoint[];
  controllers: Controller[];
  types: TypeDefinition[];
};

type ResourceGroup = {
  controller: Controller;
  endpoints: Endpoint[];
};

type ExplorerField = {
  id: string;
  label: string;
  description?: string;
  section: 'params' | 'query' | 'body';
  path: string[];
  inputType: 'string' | 'number' | 'boolean' | 'enum' | 'date' | 'json';
  required: boolean;
  nullable: boolean;
  placeholder?: string;
  enumItems?: { id: string; label: string }[];
  initialValue: string | boolean;
  multiline?: boolean;
};

type SearchConfig = {
  apiUrl: string;
  version?: string;
  resource?: string;
  operation?: string;
  instanceId?: string;
};

type LoadState =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'ready'; data: DocumentationData };

type PreparedRequest = {
  method: string | null;
  path: string | null;
  url: string;
  params: Record<string, any>;
  query: Record<string, any>;
  body: Record<string, any> | null;
  headers: Record<string, string>;
};

type RequestPreview = {
  json: PreparedRequest;
  errors: Record<string, string>;
};

type ResponseState =
  | {
      status: 'idle';
    }
  | {
      status: 'loading';
      startedAt: string;
    }
  | {
      status: 'success' | 'error';
      startedAt: string;
      finishedAt: string;
      response: {
        ok: boolean;
        status: number | null;
        statusText: string;
        headers: Record<string, string>;
        body: any;
      };
    };

let Page = styled.div`
  display: flex;
  flex-direction: column;
  height: 100dvh;
  min-height: 100%;
  overflow: hidden;
  background: ${theme.colors.background};
  color: ${theme.colors.foreground};
`;

let Topbar = styled.div`
  position: sticky;
  top: 0;
  z-index: 10;
  flex-shrink: 0;
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 16px;
  padding: 16px 20px;
  background: color-mix(in srgb, ${theme.colors.background} 92%, transparent);
  backdrop-filter: blur(10px);
  border-bottom: 1px solid ${theme.colors.gray300};

  @media (max-width: 800px) {
    grid-template-columns: 1fr;
  }
`;

let Content = styled.div`
  display: grid;
  grid-template-columns: minmax(340px, 42%) minmax(0, 58%);
  height: 100%;
  min-height: 0;
  flex: 1;
  overflow: hidden;

  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
  }
`;

let Pane = styled.div`
  height: 100%;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
`;

let LeftPane = styled(Pane)`
  padding: 20px;
  border-right: 1px solid ${theme.colors.gray300};

  @media (max-width: 1100px) {
    border-right: none;
    border-bottom: 1px solid ${theme.colors.gray300};
  }
`;

let RightPane = styled(Pane)`
  background: ${theme.colors.gray100};
`;

let MetaCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
  background: ${theme.colors.gray100};
  border: 1px solid ${theme.colors.gray300};
  border-radius: 10px;
  margin-bottom: 20px;
`;

let MetaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
`;

let Badge = styled.span<{ $tone?: 'method' | 'section' | 'info' }>`
  display: inline-flex;
  align-items: center;
  padding: 5px 9px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  border: 1px solid
    ${({ $tone }) =>
      $tone === 'method'
        ? theme.colors.gray500
        : $tone === 'section'
          ? theme.colors.gray400
          : theme.colors.gray300};
  background: ${({ $tone }) =>
    $tone === 'method'
      ? theme.colors.gray800
      : $tone === 'section'
        ? theme.colors.gray200
        : theme.colors.gray100};
  color: ${({ $tone }) =>
    $tone === 'method' ? theme.colors.background : theme.colors.gray900};
`;

let PathText = styled.code`
  display: block;
  padding: 10px 12px;
  border-radius: 10px;
  background: ${theme.colors.gray200};
  color: ${theme.colors.gray900};
  font-size: 12px;
  white-space: nowrap;
  overflow: auto;
`;

let Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: 5px;

  & + & {
    margin-top: 24px;
  }
`;

let SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

let FieldList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

let FieldCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 14px;
  border: 1px solid ${theme.colors.gray300};
  border-radius: 14px;
  background: ${theme.colors.background};
`;

let FieldTop = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
`;

let FieldMeta = styled.div`
  display: flex;
  gap: 8px;
`;

let EmptyState = styled.div`
  padding: 18px;
  border: 1px dashed ${theme.colors.gray400};
  border-radius: 14px;
  background: ${theme.colors.gray100};
`;

let JsonCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 16px;
  /* border: 1px solid ${theme.colors.gray300}; */
  border-radius: 10px;
  /* background: ${theme.colors.background}; */
`;

let CodePanel = styled.div`
  overflow: hidden;
  border-radius: 10px;
`;

let JsonHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
`;

let JsonActions = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

let RequestPreviewStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

let UrlPreviewCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
  border: 1px solid ${theme.colors.gray300};
  border-radius: 10px;
  background: linear-gradient(
    180deg,
    color-mix(in srgb, ${theme.colors.background} 88%, ${theme.colors.gray100}),
    ${theme.colors.background}
  );
`;

let UrlPreviewTop = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

let UrlPreviewText = styled.code`
  display: block;
  width: 100%;
  padding: 14px 16px;
  border-radius: 10px;
  border: 1px solid ${theme.colors.gray300};
  background: ${theme.colors.gray100};
  color: ${theme.colors.gray900};
  font-size: 13px;
  line-height: 1.5;
  white-space: pre-wrap;
  word-break: break-word;
`;

let JsonPreviewCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 16px;
  border: 1px solid ${theme.colors.gray300};
  border-radius: 10px;
  background: ${theme.colors.background};
`;

let CenterState = styled.div`
  min-height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px;
`;

let getSearchConfig = (): SearchConfig => {
  let params = new URLSearchParams(window.location.search);
  let apiUrl =
    params.get('apiUrl') ||
    params.get('api_host') ||
    params.get('baseUrl') ||
    params.get('origin') ||
    window.location.origin;

  return {
    apiUrl: getAllowedApiUrl(apiUrl, window.location.origin),
    version: params.get('version') || undefined,
    resource: params.get('resource') || undefined,
    operation: params.get('operation') || undefined,
    instanceId: params.get('instance_id') || undefined
  };
};

let getDateValue = (value: string | boolean): Date | undefined => {
  if (typeof value !== 'string' || !value.trim().length) return undefined;

  let date = new Date(value);
  if (Number.isNaN(date.getTime())) return undefined;

  return date;
};

let getJson = async <T,>(url: string): Promise<T> => {
  let response = await fetch(url);

  if (!response.ok) {
    throw new Error(`Failed to load ${url} (${response.status})`);
  }

  return (await response.json()) as T;
};

let fetchDocumentation = async (config: SearchConfig): Promise<DocumentationData> => {
  let versionsResponse = await getJson<{ versions: ApiVersion[] }>(
    `${config.apiUrl}/metorial/introspect/versions`
  );
  let selectedVersion =
    config.version ||
    versionsResponse.versions.find(version => version.isCurrent)?.version ||
    versionsResponse.versions[0]?.version;

  if (!selectedVersion) {
    throw new Error('No API versions were returned by the introspection endpoint.');
  }

  let endpointResponse = await getJson<{
    endpoints: Endpoint[];
    controllers: Controller[];
    types: TypeDefinition[];
  }>(
    `${config.apiUrl}/metorial/introspect/endpoints?version=${encodeURIComponent(selectedVersion)}`
  );

  let endpoints = endpointResponse.endpoints.filter(e => !e.hideInDocs);
  let endpointControllerMap = new Map(
    endpoints.map(endpoint => [endpoint.controllerId, endpoint])
  );

  let controllers = endpointResponse.controllers.filter(
    c => !c.deprecated && !c.hideInDocs && endpointControllerMap.has(c.id)
  );
  let controllerMap = new Map(controllers.map(controller => [controller.id, controller]));

  endpoints = endpoints.filter(e => controllerMap.has(e.controllerId));

  return {
    versions: versionsResponse.versions,
    endpoints: endpoints,
    controllers: controllers,
    types: endpointResponse.types
  };
};

let getPreferredPath = (endpoint: Endpoint): ApiPath => {
  let rankedPaths = [...endpoint.allPaths].sort((a, b) => {
    let rank = (path: ApiPath) => {
      if (path.sdkPath.startsWith('dashboard.')) return 3;
      if (path.sdkPath.startsWith('management.')) return 2;
      if (path.sdkPath.startsWith('consumer.')) return 1;
      return 0;
    };

    return rank(a) - rank(b);
  });

  return rankedPaths[0] || endpoint.allPaths[0];
};

let getOperationKey = (endpoint: Endpoint) => {
  let preferredPath = getPreferredPath(endpoint);
  let sdkPart = preferredPath.sdkPath.split('.').pop();

  return sdkPart || endpoint.name;
};

let formatOperationLabel = (endpoint: Endpoint) => {
  let key = getOperationKey(endpoint);
  let labelMap: Record<string, string> = {
    list: 'List',
    get: 'Get',
    create: 'Create',
    update: 'Update',
    delete: 'Delete',
    patch: 'Patch',
    search: 'Search'
  };

  return labelMap[key] || endpoint.name;
};

let getOperationSortWeight = (endpoint: Endpoint) => {
  let order = ['list', 'get', 'create', 'update', 'patch', 'delete'];
  let index = order.indexOf(getOperationKey(endpoint));

  return index === -1 ? order.length : index;
};

let buildGroups = (data: DocumentationData): ResourceGroup[] => {
  let groups = data.controllers
    .map(controller => ({
      controller,
      endpoints: data.endpoints
        .filter(endpoint => endpoint.controllerId === controller.id && !endpoint.hideInDocs)
        .sort((a, b) => {
          let weightDifference = getOperationSortWeight(a) - getOperationSortWeight(b);
          if (weightDifference !== 0) return weightDifference;

          return formatOperationLabel(a).localeCompare(formatOperationLabel(b));
        })
    }))
    .filter(group => group.endpoints.length > 0);

  return groups.sort((a, b) => a.controller.name.localeCompare(b.controller.name));
};

let getTypeIndex = (data: DocumentationData) => {
  return new Map(data.types.map(type => [type.id, type.type]));
};

let titleizePath = (path: string[]) => {
  return path.join('.');
};

let getExampleValue = (type: IntrospectedType): any => {
  if (type.examples?.length) {
    return type.examples[0];
  }

  if (type.type === 'enum' && type.items?.length) {
    return type.items[0];
  }

  if (type.type === 'boolean') return false;
  if (type.type === 'number') return 0;
  if (type.type === 'date' || type.type === 'datetime') return new Date().toISOString();
  if (type.type === 'string') return 'example';

  if (type.type === 'array') {
    let item = type.items?.[0];
    return item ? [getExampleValue(item)] : [];
  }

  if (type.type === 'union') {
    return type.items?.[0] ? getExampleValue(type.items[0]) : null;
  }

  if (type.type === 'intersection') {
    let result: Record<string, any> = {};

    for (let item of type.items || []) {
      let nextValue = getExampleValue(item);
      if (nextValue && typeof nextValue === 'object' && !Array.isArray(nextValue)) {
        result = { ...result, ...nextValue };
      }
    }

    return result;
  }

  if (type.properties) {
    let result: Record<string, any> = {};
    for (let [key, property] of Object.entries(type.properties)) {
      result[key] = getExampleValue(property);
    }
    return result;
  }

  return null;
};

let getEnumItems = (type: IntrospectedType): { id: string; label: string }[] | undefined => {
  if (type.type === 'enum') {
    let rawValues = [
      ...(type.examples || []),
      ...((type.items || []).map(item => {
        return (
          item.name ??
          (item.examples?.length ? item.examples[0] : undefined) ??
          (item.type === 'literal' && item.description ? item.description : undefined)
        );
      }) || [])
    ];

    let items = rawValues
      .flatMap(value => {
        if (Array.isArray(value) && value.length >= 1) {
          return [value[0]];
        }

        return [value];
      })
      .filter(value => value !== undefined && value !== null)
      .map((value, index) => ({
        id: String(value),
        label: String(value ?? index)
      }))
      .filter((item, index, array) => array.findIndex(i => i.id === item.id) === index);

    return items.length ? items : undefined;
  }

  if (type.type === 'literal') {
    let literalValue =
      type.name ?? (type.examples?.length ? type.examples[0] : undefined) ?? type.description;

    if (literalValue === undefined || literalValue === null) {
      return undefined;
    }

    return [{ id: String(literalValue), label: String(literalValue) }];
  }

  if (type.type === 'union' && type.items?.length) {
    let items = type.items.flatMap(item => getEnumItems(item) || []);
    let deduped = items.filter(
      (item, index, array) => array.findIndex(other => other.id === item.id) === index
    );

    return deduped.length ? deduped : undefined;
  }

  return undefined;
};

let isStringLikeUnion = (type: IntrospectedType): boolean => {
  if (type.type !== 'union' || !type.items?.length) return false;

  return type.items.every(item => {
    if (item.type === 'string') return true;

    if (item.type === 'array' && item.items?.length === 1) {
      return item.items[0]?.type === 'string';
    }

    return false;
  });
};

let getPrimitiveInputType = (
  type: IntrospectedType
): ExplorerField['inputType'] | undefined => {
  if (type.type === 'boolean') return 'boolean';
  if (type.type === 'number') return 'number';
  if (getEnumItems(type)?.length) return 'enum';
  if (isStringLikeUnion(type)) return 'string';
  if (type.type === 'date' || type.type === 'datetime') return 'date';
  if (type.type === 'string' || type.type === 'file') return 'string';

  return undefined;
};

let getFieldInitialValue = (
  type: IntrospectedType,
  path: string[],
  inputType: ExplorerField['inputType']
): string | boolean => {
  if (inputType === 'boolean') {
    return false;
  }

  if (inputType === 'enum') {
    return '';
  }

  if (inputType === 'number') {
    return '';
  }

  if (inputType === 'date') {
    return '';
  }

  if (inputType === 'json') {
    return '';
  }

  return '';
};

let createField = (
  section: ExplorerField['section'],
  path: string[],
  type: IntrospectedType
): ExplorerField => {
  let primitiveInputType = getPrimitiveInputType(type);
  let inputType = primitiveInputType || 'json';

  return {
    id: `${section}:${path.join('.')}`,
    label: titleizePath(path),
    description: type.description,
    section,
    path,
    inputType,
    required: !type.optional,
    nullable: type.nullable,
    placeholder: type.description,
    enumItems: inputType === 'enum' ? getEnumItems(type) : undefined,
    initialValue: getFieldInitialValue(type, path, inputType),
    multiline: inputType === 'json'
  };
};

let flattenTypeFields = (
  section: ExplorerField['section'],
  type: IntrospectedType | undefined,
  prefix: string[] = []
): ExplorerField[] => {
  if (!type) return [];

  let primitiveInputType = getPrimitiveInputType(type);
  if (primitiveInputType) {
    return [createField(section, prefix, type)];
  }

  if (type.type === 'object' && type.properties) {
    return Object.entries(type.properties).flatMap(([key, property]) =>
      flattenTypeFields(section, property, [...prefix, key])
    );
  }

  if (type.type === 'intersection' && type.items?.length) {
    return type.items.flatMap(item => flattenTypeFields(section, item, prefix));
  }

  if (type.type === 'array' || type.type === 'union' || type.type === 'record') {
    return [createField(section, prefix, type)];
  }

  if (type.properties) {
    return Object.entries(type.properties).flatMap(([key, property]) =>
      flattenTypeFields(section, property, [...prefix, key])
    );
  }

  return [createField(section, prefix, type)];
};

let getParamFields = (endpoint: Endpoint) => {
  let preferredPath = getPreferredPath(endpoint);
  let paramNames = Array.from(preferredPath.path.matchAll(/:(\w+)/g)).map(match => match[1]);

  return paramNames.map<ExplorerField>(name => ({
    id: `params:${name}`,
    label: name,
    section: 'params',
    path: [name],
    inputType: 'string',
    required: true,
    nullable: false,
    initialValue: ''
  }));
};

let buildFields = (
  endpoint: Endpoint | undefined,
  typeIndex: Map<string, IntrospectedType>
) => {
  if (!endpoint) return [];

  let fields = [
    ...getParamFields(endpoint),
    ...flattenTypeFields(
      'query',
      endpoint.queryId ? typeIndex.get(endpoint.queryId) : undefined
    ),
    ...flattenTypeFields('body', endpoint.bodyId ? typeIndex.get(endpoint.bodyId) : undefined)
  ];

  let deduped = new Map<string, ExplorerField>();
  for (let field of fields) {
    deduped.set(field.id, field);
  }

  return Array.from(deduped.values());
};

let createInitialValues = (fields: ExplorerField[]) => {
  let values: Record<string, string | boolean> = {};

  for (let field of fields) {
    values[field.id] = field.initialValue;
  }

  return values;
};

let setNestedValue = (target: Record<string, any>, path: string[], value: any) => {
  let current = target;

  for (let index = 0; index < path.length - 1; index++) {
    let key = path[index];
    let nextValue = current[key];

    if (!nextValue || typeof nextValue !== 'object' || Array.isArray(nextValue)) {
      current[key] = {};
    }

    current = current[key];
  }

  current[path[path.length - 1]] = value;
};

let buildRequestPreview = (
  endpoint: Endpoint | undefined,
  fields: ExplorerField[],
  formValues: Record<string, string | boolean>,
  apiUrl: string,
  instanceId?: string
): RequestPreview => {
  let errors: Record<string, string> = {};
  let params: Record<string, any> = {};
  let query: Record<string, any> = {};
  let body: Record<string, any> = {};

  for (let field of fields) {
    let rawValue = formValues[field.id];
    let parsedValue: any = rawValue;

    if (field.inputType === 'json') {
      let jsonValue = typeof rawValue === 'string' ? rawValue.trim() : '';

      if (!jsonValue) {
        if (field.required) {
          errors[field.id] = 'JSON value is required.';
        }
        continue;
      }

      try {
        parsedValue = JSON.parse(jsonValue);
      } catch {
        errors[field.id] = 'Invalid JSON.';
        continue;
      }
    } else if (field.inputType === 'number') {
      let normalized = String(rawValue).trim();
      if (!normalized.length) {
        if (field.required) {
          errors[field.id] = 'Number is required.';
        }
        continue;
      }

      parsedValue = Number(normalized);
      if (Number.isNaN(parsedValue)) {
        errors[field.id] = 'Invalid number.';
        continue;
      }
    } else if (
      field.inputType === 'string' ||
      field.inputType === 'date' ||
      field.inputType === 'enum'
    ) {
      let normalized = String(rawValue).trim();
      if (!normalized.length) {
        if (field.required) {
          errors[field.id] = 'Value is required.';
        }
        continue;
      }
      parsedValue = normalized;
    } else if (field.inputType === 'boolean') {
      parsedValue = !!rawValue;
    }

    if (field.section === 'params') {
      setNestedValue(params, field.path, parsedValue);
    } else if (field.section === 'query') {
      setNestedValue(query, field.path, parsedValue);
    } else {
      setNestedValue(body, field.path, parsedValue);
    }
  }

  let preferredPath = endpoint ? getPreferredPath(endpoint).path : '';
  let resolvedPath = preferredPath.replace(/:(\w+)/g, (_, name) => {
    let value = params[name];
    return value ? encodeURIComponent(String(value)) : `:${name}`;
  });

  let queryString = qs.stringify(query, {
    arrayFormat: 'repeat',
    skipNulls: true
  });
  let url = resolvedPath ? `${apiUrl}${resolvedPath}` : apiUrl;
  if (queryString) {
    url = `${url}?${queryString}`;
  }

  let method = endpoint?.method.toUpperCase() || null;
  let shouldSendBody = method && !['GET', 'HEAD'].includes(method);
  let normalizedBody = shouldSendBody ? body : null;
  let headers: Record<string, string> = {};
  if (instanceId) {
    headers['metorial-instance-id'] = instanceId;
  }
  if (normalizedBody) {
    headers['content-type'] = 'application/json';
  }

  return {
    json: {
      method,
      path: resolvedPath || null,
      url,
      params,
      query,
      body: normalizedBody,
      headers
    },
    errors
  };
};

let getSectionLabel = (section: ExplorerField['section']) => {
  if (section === 'params') return 'Path Parameters';
  if (section === 'query') return 'Query Parameters';
  return 'Request Body';
};

let getSectionDescription = (section: ExplorerField['section']) => {
  // if (section === 'params') return 'Values injected into the route path.';
  // if (section === 'query') return 'Optional filters and modifiers sent as query params.';
  // return 'JSON payload derived from the request schema.';
};

export let ExplorerApp = () => {
  let [config] = useState(() => getSearchConfig());
  let [state, setState] = useState<LoadState>({ status: 'loading' });
  let [selectedResourceId, setSelectedResourceId] = useState('');
  let [selectedEndpointId, setSelectedEndpointId] = useState('');
  let [formValues, setFormValues] = useState<Record<string, string | boolean>>({});
  let [requestView, setRequestView] = useState<'request' | 'response'>('request');
  let [responseState, setResponseState] = useState<ResponseState>({ status: 'idle' });

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        let data = await fetchDocumentation(config);
        if (cancelled) return;

        setState({ status: 'ready', data });
      } catch (error) {
        if (cancelled) return;

        setState({
          status: 'error',
          message:
            error instanceof Error
              ? error.message
              : 'Something went wrong while loading API introspection data.'
        });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [config]);

  let groups = useMemo(() => {
    if (state.status !== 'ready') return [];
    return buildGroups(state.data);
  }, [state]);

  useEffect(() => {
    if (!groups.length) return;

    let matchingResource =
      groups.find(group => {
        let query = config.resource?.toLowerCase();
        if (!query) return false;

        return (
          group.controller.id.toLowerCase() === query ||
          group.controller.name.toLowerCase() === query
        );
      }) || groups[0];

    let nextResourceId = matchingResource.controller.id;
    let nextEndpoint =
      matchingResource.endpoints.find(endpoint => {
        let query = config.operation?.toLowerCase();
        if (!query) return false;

        return (
          endpoint.id.toLowerCase() === query ||
          endpoint.name.toLowerCase() === query ||
          getOperationKey(endpoint).toLowerCase() === query
        );
      }) || matchingResource.endpoints[0];

    setSelectedResourceId(current => current || nextResourceId);
    setSelectedEndpointId(current => current || nextEndpoint?.id || '');
  }, [config.operation, config.resource, groups]);

  let activeGroup =
    groups.find(group => group.controller.id === selectedResourceId) || groups[0] || null;

  useEffect(() => {
    if (!activeGroup) return;

    let currentEndpointExists = activeGroup.endpoints.some(
      endpoint => endpoint.id === selectedEndpointId
    );
    if (!currentEndpointExists) {
      setSelectedEndpointId(activeGroup.endpoints[0]?.id || '');
    }
  }, [activeGroup, selectedEndpointId]);

  let activeEndpoint =
    activeGroup?.endpoints.find(endpoint => endpoint.id === selectedEndpointId) ||
    activeGroup?.endpoints[0] ||
    null;

  let typeIndex = useMemo(() => {
    if (state.status !== 'ready') return new Map<string, IntrospectedType>();
    return getTypeIndex(state.data);
  }, [state]);

  let fields = useMemo(
    () => buildFields(activeEndpoint || undefined, typeIndex),
    [activeEndpoint, typeIndex]
  );

  useEffect(() => {
    setFormValues(createInitialValues(fields));
  }, [activeEndpoint?.id]);

  let groupedFields = useMemo(() => {
    return {
      params: fields.filter(field => field.section === 'params'),
      query: fields.filter(field => field.section === 'query'),
      body: fields.filter(field => field.section === 'body')
    };
  }, [fields]);

  let preview = useMemo(
    () =>
      buildRequestPreview(
        activeEndpoint || undefined,
        fields,
        formValues,
        config.apiUrl,
        config.instanceId
      ),
    [activeEndpoint, fields, formValues, config.apiUrl, config.instanceId]
  );

  useEffect(() => {
    setRequestView('request');
    setResponseState({ status: 'idle' });
  }, [activeEndpoint?.id]);

  let hasErrors = Object.keys(preview.errors).length > 0;

  let sendRequest = async () => {
    let startedAt = new Date().toISOString();
    setRequestView('response');
    setResponseState({ status: 'loading', startedAt });

    try {
      let request = preview.json;
      let response = await fetch(request.url, {
        method: request.method || 'GET',
        headers: request.headers,
        body: request.body ? JSON.stringify(request.body) : undefined,
        credentials: 'include'
      });

      let responseText = await response.text();
      let contentType = response.headers.get('content-type') || '';
      let parsedBody: any = responseText;

      if (responseText.length && contentType.includes('application/json')) {
        try {
          parsedBody = JSON.parse(responseText);
        } catch {
          parsedBody = responseText;
        }
      }

      setResponseState({
        status: response.ok ? 'success' : 'error',
        startedAt,
        finishedAt: new Date().toISOString(),
        response: {
          ok: response.ok,
          status: response.status,
          statusText: response.statusText,
          headers: Object.fromEntries(response.headers.entries()),
          body: parsedBody
        }
      });
    } catch (error) {
      setResponseState({
        status: 'error',
        startedAt,
        finishedAt: new Date().toISOString(),
        response: {
          ok: false,
          status: null,
          statusText: error instanceof Error ? error.message : 'Request failed',
          headers: {},
          body: {
            error: error instanceof Error ? error.message : 'Request failed'
          }
        }
      });
    }
  };

  if (state.status === 'loading') {
    return (
      <CenterState>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Spinner />
          <Text size="3" color="gray800">
            Loading API explorer
          </Text>
        </div>
      </CenterState>
    );
  }

  if (state.status === 'error') {
    return (
      <CenterState>
        <JsonCard style={{ maxWidth: 720, width: '100%' }}>
          <Title as="h1" size="5" weight="medium">
            Unable to load API data
          </Title>
          <Text size="3" color="gray800">
            {state.message}
          </Text>
          <CodePanel>
            <CodeBlock
              code={JSON.stringify(
                { apiUrl: config.apiUrl, version: config.version },
                null,
                2
              )}
              language="json"
              lineNumbers={false}
            />
          </CodePanel>
        </JsonCard>
      </CenterState>
    );
  }

  if (!activeGroup || !activeEndpoint) {
    return (
      <CenterState>
        <JsonCard style={{ maxWidth: 720, width: '100%' }}>
          <Title as="h1" size="5" weight="medium">
            No documented endpoints available
          </Title>
          <Text size="3" color="gray800">
            The introspection endpoint responded successfully, but there were no visible
            resources to render.
          </Text>
        </JsonCard>
      </CenterState>
    );
  }

  let preferredPath = getPreferredPath(activeEndpoint);

  return (
    <Page>
      <Topbar>
        <Select
          label="Resource"
          value={activeGroup.controller.id}
          onChange={value => {
            setSelectedResourceId(value);
          }}
          items={groups.map(group => ({
            id: group.controller.id,
            label: group.controller.name
          }))}
        />

        <Select
          label="Operation"
          value={activeEndpoint.id}
          onChange={value => {
            setSelectedEndpointId(value);
          }}
          items={activeGroup.endpoints.map(endpoint => ({
            id: endpoint.id,
            label: `${formatOperationLabel(endpoint)} · ${endpoint.method.toUpperCase()}`
          }))}
        />
      </Topbar>

      <Content>
        <LeftPane>
          <MetaCard>
            <MetaRow>
              <Badge $tone="method">{activeEndpoint.method.toUpperCase()}</Badge>
              <Badge $tone="info">{activeGroup.controller.name}</Badge>
              <Badge $tone="info">{formatOperationLabel(activeEndpoint)}</Badge>
            </MetaRow>

            <div>
              <Title as="h1" size="5" weight="medium">
                {activeEndpoint.name}
              </Title>
              <div style={{ height: 6 }} />
              <Text size="3" color="gray800">
                {activeEndpoint.description || activeGroup.controller.description}
              </Text>
            </div>

            <PathText>{preferredPath.path}</PathText>
          </MetaCard>

          {(['params', 'query', 'body'] as const).map(section => (
            <Section key={section}>
              <SectionHeader>
                <div>
                  <Text size="2" color="gray700" weight="strong">
                    {getSectionLabel(section)}
                  </Text>
                  {/* <div style={{ height: 6 }} />
                  <Text size="2" color="gray700">
                    {getSectionDescription(section)}
                  </Text> */}
                </div>
              </SectionHeader>

              {groupedFields[section].length ? (
                <FieldList>
                  {groupedFields[section].map(field => (
                    <FieldCard key={field.id}>
                      <FieldTop>
                        <div>
                          <Text as="div" size="3" weight="medium">
                            <code>{field.label}</code>
                          </Text>
                          {field.description && (
                            <>
                              <div style={{ height: 4 }} />
                              <Text size="2" color="gray700">
                                {field.description}
                              </Text>
                            </>
                          )}
                        </div>

                        <FieldMeta>
                          <Badge $tone="info">{field.inputType}</Badge>
                          <Badge $tone="info">
                            {field.required ? 'required' : 'optional'}
                          </Badge>
                        </FieldMeta>
                      </FieldTop>

                      {field.inputType === 'enum' ? (
                        <Select
                          label={field.label}
                          hideLabel
                          value={String(formValues[field.id] || '')}
                          onChange={value => {
                            setFormValues(current => ({ ...current, [field.id]: value }));
                          }}
                          items={field.enumItems || []}
                        />
                      ) : field.inputType === 'date' ? (
                        <DatePicker
                          label={field.label}
                          hideLabel
                          type="single"
                          placeholder="Select date"
                          value={getDateValue(formValues[field.id])}
                          onChange={value => {
                            setFormValues(current => ({
                              ...current,
                              [field.id]: value.toISOString()
                            }));
                          }}
                        />
                      ) : field.inputType === 'boolean' ? (
                        <Checkbox
                          label={field.label}
                          hideLabel
                          checked={Boolean(formValues[field.id])}
                          onCheckedChange={checked => {
                            setFormValues(current => ({ ...current, [field.id]: checked }));
                          }}
                        />
                      ) : field.inputType === 'json' ? (
                        <CodeEditor
                          label={field.label}
                          description={field.description}
                          lang="json"
                          height="220px"
                          value={String(formValues[field.id] || '')}
                          onChange={value => {
                            setFormValues(current => ({ ...current, [field.id]: value }));
                          }}
                        />
                      ) : (
                        <Input
                          label={field.label}
                          hideLabel
                          as={field.multiline ? 'textarea' : 'input'}
                          minRows={field.multiline ? 5 : undefined}
                          type={field.inputType === 'number' ? 'number' : 'text'}
                          value={String(formValues[field.id] || '')}
                          placeholder={field.placeholder}
                          error={preview.errors[field.id]}
                          onInput={value => {
                            setFormValues(current => ({ ...current, [field.id]: value }));
                          }}
                        />
                      )}
                    </FieldCard>
                  ))}
                </FieldList>
              ) : (
                <EmptyState>
                  <Text size="3" color="gray700">
                    This operation does not define any {getSectionLabel(section).toLowerCase()}
                    .
                  </Text>
                </EmptyState>
              )}
            </Section>
          ))}
        </LeftPane>

        <RightPane>
          <JsonCard>
            <JsonHeader>
              <div>
                <Title as="h2" size="3" weight="medium">
                  Request Runner
                </Title>
                <Text size="2" color="gray600" weight="medium">
                  Send a request to the Metorial API.
                </Text>
              </div>

              <JsonActions>
                <OptionToggle
                  ariaLabel="View request or response"
                  value={requestView}
                  size="2"
                  onChange={value => {
                    if (value === 'request' || value === 'response') {
                      setRequestView(value);
                    }
                  }}
                  items={[
                    { id: 'request', label: 'Request' },
                    { id: 'response', label: 'Response' }
                  ]}
                />

                <Button
                  variant="solid"
                  size="2"
                  disabled={hasErrors || responseState.status === 'loading'}
                  loading={responseState.status === 'loading'}
                  onClick={() => {
                    void sendRequest();
                  }}
                >
                  Send Request
                </Button>
              </JsonActions>
            </JsonHeader>

            {hasErrors ? (
              <EmptyState>
                <Text size="3" color="gray700">
                  Fix the invalid request fields on the left before sending this request.
                </Text>
              </EmptyState>
            ) : null}

            <CodePanel>
              {requestView === 'request' ? (
                <RequestPreviewStack>
                  <UrlPreviewCard>
                    <UrlPreviewTop>
                      <Badge $tone="method">{preview.json.method || 'GET'}</Badge>
                      <Text size="2" color="gray700" weight="medium">
                        URL
                      </Text>
                    </UrlPreviewTop>

                    <UrlPreviewText>{preview.json.url}</UrlPreviewText>
                  </UrlPreviewCard>

                  {preview.json.body && (
                    <JsonPreviewCard>
                      <div>
                        <Title as="h3" size="2" weight="medium">
                          Request Body
                        </Title>
                      </div>

                      <CodePanel>
                        <CodeBlock
                          code={JSON.stringify(preview.json.body, null, 2)}
                          language="json"
                          lineNumbers={false}
                        />
                      </CodePanel>
                    </JsonPreviewCard>
                  )}
                </RequestPreviewStack>
              ) : responseState.status === 'idle' ? (
                <EmptyState>
                  <Text size="3" color="gray700">
                    No response yet. Send the request to inspect the returned payload.
                  </Text>
                </EmptyState>
              ) : responseState.status === 'loading' ? (
                <EmptyState>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <Spinner />
                    <Text size="3" color="gray700">
                      Sending request...
                    </Text>
                  </div>
                </EmptyState>
              ) : (
                <CodeBlock
                  code={JSON.stringify(responseState.response.body || {}, null, 2)}
                  language="json"
                  lineNumbers={false}
                />
              )}
            </CodePanel>
          </JsonCard>
        </RightPane>
      </Content>
    </Page>
  );
};
